import type { Account, AccountLedgerEntry, Plan } from '../types';
import { addDays, addMonths, normalizeDateISO, todayISO } from './date';

export type StatementSort = 'newest' | 'oldest' | 'highest' | 'lowest';
export type StatementView = 'statement' | 'table' | 'cards';

export type StatementRow = AccountLedgerEntry & {
  particulars: string;
  reference: string;
  credit: number;
  debit: number;
  balance: number;
};

export type StatementSummary = {
  rows: StatementRow[];
  displayRows: StatementRow[];
  openingBalance: number;
  closingBalance: number;
  totalCredit: number;
  totalDebit: number;
  netMovement: number;
};

const timestampValue = (value: unknown) => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    const maybeTimestamp = value as { seconds?: number; nanoseconds?: number; toMillis?: () => number };
    if (typeof maybeTimestamp.toMillis === 'function') return String(maybeTimestamp.toMillis());
    if (typeof maybeTimestamp.seconds === 'number') return `${String(maybeTimestamp.seconds).padStart(12, '0')}.${String(maybeTimestamp.nanoseconds || 0).padStart(9, '0')}`;
  }
  return '';
};

const chronologicalLedgerCompare = (a: AccountLedgerEntry, b: AccountLedgerEntry) => {
  const dateCompare = normalizeDateISO(a.dateISO || a.date).localeCompare(normalizeDateISO(b.dateISO || b.date));
  if (dateCompare) return dateCompare;
  const createdCompare = timestampValue(a.createdAt || a.updatedAt).localeCompare(timestampValue(b.createdAt || b.updatedAt));
  if (createdCompare) return createdCompare;
  return String(a.id || '').localeCompare(String(b.id || ''));
};

const reverseChronologicalLedgerCompare = (a: StatementRow, b: StatementRow) => {
  const dateCompare = normalizeDateISO(b.dateISO || b.date).localeCompare(normalizeDateISO(a.dateISO || a.date));
  if (dateCompare) return dateCompare;
  const createdCompare = timestampValue(b.createdAt || b.updatedAt).localeCompare(timestampValue(a.createdAt || a.updatedAt));
  if (createdCompare) return createdCompare;
  return String(b.id || '').localeCompare(String(a.id || ''));
};

export const ledgerRangePreset = (preset: 'today' | 'week' | 'month' | 'lastMonth' | 'fy' | 'custom') => {
  const today = todayISO();
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week') {
    const day = new Date(today + 'T00:00:00').getDay();
    return { from: addDays(today, day === 0 ? -6 : 1 - day), to: today };
  }
  if (preset === 'lastMonth') {
    const firstThisMonth = `${today.slice(0, 7)}-01`;
    const firstLastMonth = addMonths(firstThisMonth, -1);
    return { from: firstLastMonth, to: addDays(firstThisMonth, -1) };
  }
  if (preset === 'fy') {
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    const fyStart = month >= 4 ? year : year - 1;
    return { from: `${fyStart}-04-01`, to: `${fyStart + 1}-03-31` };
  }
  return { from: `${today.slice(0, 7)}-01`, to: today };
};

const rowParticulars = (row: AccountLedgerEntry) => {
  if (row.type === 'transfer_out') return `Transfer to account${row.relatedAccountId ? ` ${row.relatedAccountId}` : ''}`;
  if (row.type === 'transfer_in') return `Transfer from account${row.relatedAccountId ? ` ${row.relatedAccountId}` : ''}`;
  if (row.type === 'credit_card_payment') return 'Credit card payment';
  if (row.type === 'plan_payment') return 'Plan payment';
  if (row.type === 'credit_card_spend') return row.note || 'Credit card spend';
  return row.note || row.type.replace(/_/g, ' ');
};

export function buildAccountStatement(account: Account, ledger: AccountLedgerEntry[], from: string, to: string, sort: StatementSort = 'newest', search = ''): StatementSummary {
  const fromISO = normalizeDateISO(from);
  const toISO = normalizeDateISO(to);
  const chronological = [...ledger].sort(chronologicalLedgerCompare);
  const beforeRange = chronological.filter((row) => normalizeDateISO(row.dateISO || row.date) < fromISO);
  const openingBalance = beforeRange.reduce((balance, row) => balance + (row.direction === 'credit' ? row.amount : -row.amount), Number(account.openingBalance || 0));
  let running = openingBalance;
  const needle = search.trim().toLowerCase();
  const rows = chronological
    .filter((row) => {
      const date = normalizeDateISO(row.dateISO || row.date);
      return date >= fromISO && date <= toISO;
    })
    .map((row) => {
      const amount = Number(row.amount || 0);
      const credit = row.direction === 'credit' ? amount : 0;
      const debit = row.direction === 'debit' ? amount : 0;
      running += credit - debit;
      return {
        ...row,
        dateISO: normalizeDateISO(row.dateISO || row.date),
        particulars: rowParticulars(row),
        reference: row.transactionId || row.transferId || row.cardPaymentId || row.planPaymentId || row.id,
        credit,
        debit,
        balance: running
      } satisfies StatementRow;
    })
    .filter((row) => !needle || [row.particulars, row.note, row.reference, row.type].some((value) => String(value || '').toLowerCase().includes(needle)));
  const displayRows = [...rows].sort((a, b) => {
    if (sort === 'oldest') return chronologicalLedgerCompare(a, b);
    if (sort === 'highest') return b.amount - a.amount;
    if (sort === 'lowest') return a.amount - b.amount;
    return reverseChronologicalLedgerCompare(a, b);
  });
  const totalCredit = rows.reduce((total, row) => total + row.credit, 0);
  const totalDebit = rows.reduce((total, row) => total + row.debit, 0);
  return { rows, displayRows, openingBalance, closingBalance: openingBalance + totalCredit - totalDebit, totalCredit, totalDebit, netMovement: totalCredit - totalDebit };
}

export const summarizeTransactions = <T extends { direction?: 'credit' | 'debit'; type?: string; amount: number }>(rows: T[]) => {
  const credit = rows.reduce((total, row) => total + (row.direction === 'credit' || row.type === 'Income' ? Number(row.amount || 0) : 0), 0);
  const debit = rows.reduce((total, row) => total + (row.direction === 'debit' || row.type === 'Expense' || row.type === 'Investment' ? Number(row.amount || 0) : 0), 0);
  return { credit, debit, net: credit - debit, count: rows.length };
};

export const summarizePlans = (plans: Plan[]) => {
  const total = plans.reduce((sum, plan) => sum + Number(plan.amount || 0), 0);
  const byCategory = plans.reduce<Record<string, number>>((map, plan) => {
    const key = plan.category || 'Other';
    map[key] = (map[key] || 0) + Number(plan.amount || 0);
    return map;
  }, {});
  const byDate = plans.reduce<Record<string, Plan[]>>((map, plan) => {
    const key = normalizeDateISO(plan.nextDueDate || plan.dueDate);
    map[key] = [...(map[key] || []), plan];
    return map;
  }, {});
  return { total, count: plans.length, byCategory, byDate };
};
